import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Image, Pause, Play } from "lucide-react";
import { useTranslation } from "react-i18next";

import { fetch_screenshot } from "../../api";

export type ScreenshotRef = {
  recordingId: string;
  filename: string;
  userId?: string;
  label?: string;
};

/** Where a screenshot's image is loaded from. A change of resolver reloads every image, so
 * pass a stable reference (module-level, or memoized). */
export type ScreenshotUrlResolver = (screenshot: ScreenshotRef) => Promise<string>;

/** Synced recordings: a presigned download URL from the company router. */
const routerScreenshotUrl: ScreenshotUrlResolver = ({ recordingId, filename }) =>
  fetch_screenshot(recordingId, filename);

type ScreenshotCarouselProps = {
  screenshots: ScreenshotRef[];
  initialIndex?: number;
  autoFocus?: boolean;
  fullscreen?: boolean;
  onIndexChange?: (index: number) => void;
  counterLabel?: React.ReactNode;
  navigationDetails?: React.ReactNode;
  className?: string;
  resolveUrl?: ScreenshotUrlResolver;
  /** Whether a screenshot shows greyed out, e.g. one left out of a selection. */
  isDimmed?: (screenshot: ScreenshotRef) => boolean;
  /** A control acting on the current screenshot, shown with the navigation. */
  slideAction?: (screenshot: ScreenshotRef) => React.ReactNode;
};

const SCREENSHOT_PLAYBACK_SPEEDS = [5, 2, 1, 0.5, 0.2] as const;
type ScreenshotPlaybackSpeed = (typeof SCREENSHOT_PLAYBACK_SPEEDS)[number];

const formatPlaybackSpeed = (speed: ScreenshotPlaybackSpeed) => `${speed}x`;

const screenshotKey = (screenshot: ScreenshotRef) =>
  `${screenshot.recordingId}\0${screenshot.filename}`;

const clampIndex = (index: number, length: number) =>
  length > 0 ? Math.min(Math.max(index, 0), length - 1) : 0;

const ScreenshotCarousel = ({
  screenshots,
  initialIndex = 0,
  autoFocus = true,
  fullscreen = false,
  onIndexChange,
  counterLabel,
  navigationDetails,
  className = "",
  resolveUrl = routerScreenshotUrl,
  isDimmed,
  slideAction,
}: ScreenshotCarouselProps) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(() => clampIndex(initialIndex, screenshots.length));
  const [autoplay, setAutoplay] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<ScreenshotPlaybackSpeed>(1);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  // A URL is ready only after its image has loaded and decoded.
  const [loaded, setLoaded] = useState<Map<string, string | null>>(new Map());
  // Requests are only reusable while the resolver they came from is in use.
  const requests = useMemo(() => new Map<string, Promise<string>>(), [resolveUrl]);
  const [displayed, setDisplayed] = useState<{
    key: string;
    url: string;
    label: string;
  } | null>(null);
  const currentScreenshot = screenshots[clampIndex(index, screenshots.length)];
  const currentKey = currentScreenshot && screenshotKey(currentScreenshot);
  const current = currentKey ? loaded.get(currentKey) : undefined;
  const dimmed = !!currentScreenshot && !!isDimmed?.(currentScreenshot);
  const currentLabel =
    currentScreenshot?.label ??
    t("screenshot_n_of_total", { current: index + 1, total: screenshots.length });
  const readyImage =
    currentKey && current ? { key: currentKey, url: current, label: currentLabel } : null;
  // Keep the last decoded frame mounted while a new selection is loading.
  const visibleImage =
    readyImage ??
    (current === undefined &&
    displayed &&
    screenshots.some(shot => screenshotKey(shot) === displayed.key)
      ? displayed
      : null);

  useEffect(() => {
    if (currentKey && current) setDisplayed({ key: currentKey, url: current, label: currentLabel });
  }, [currentKey, current, currentLabel]);

  const move = useCallback(
    (direction: number) => {
      const nextIndex = (index + direction + screenshots.length) % screenshots.length;
      setIndex(nextIndex);
      onIndexChange?.(nextIndex);
    },
    [index, screenshots.length, onIndexChange],
  );
  const prev = useCallback(() => move(-1), [move]);
  const next = useCallback(() => move(1), [move]);
  const toggleAutoplay = useCallback(() => {
    if (screenshots.length <= 1) return;
    setAutoplay(value => !value);
  }, [screenshots.length]);

  useEffect(() => {
    setIndex(clampIndex(initialIndex, screenshots.length));
  }, [initialIndex, screenshots.length]);

  useEffect(() => {
    if (autoFocus) {
      carouselRef.current?.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  useEffect(() => {
    let cancelled = false;

    // Preload the visible image and its neighbours; keep requests reusable when
    // navigation changes while an image is still downloading.
    const nearby = [
      ...new Set([
        index,
        (index + 1) % screenshots.length,
        (index - 1 + screenshots.length) % screenshots.length,
      ]),
    ]
      .map(i => screenshots[i])
      .filter(Boolean);
    nearby.forEach(screenshot => {
      const key = screenshotKey(screenshot);
      let request = requests.get(key);
      if (!request) {
        request = resolveUrl(screenshot).then(async url => {
          const image = new window.Image();
          image.src = url;
          await image.decode();
          return url;
        });
        requests.set(key, request);
      }
      request.then(
        url => {
          if (!cancelled) setLoaded(prev => new Map(prev).set(key, url));
        },
        () => {
          if (!cancelled) setLoaded(prev => new Map(prev).set(key, null));
        },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [screenshots, index, requests, resolveUrl]);

  useEffect(() => {
    if (!autoplay || screenshots.length <= 1 || current === undefined) return;
    const id = setTimeout(next, 1000 / playbackSpeed);
    return () => clearTimeout(id);
  }, [autoplay, playbackSpeed, screenshots.length, next, current]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

      const target = event.target as HTMLElement | null;
      const targetTag = target?.tagName;
      const isTextEntryTarget =
        target?.isContentEditable || targetTag === "INPUT" || targetTag === "TEXTAREA";

      // Let the counter help button and its tooltip handle their own keys.
      if (
        isTextEntryTarget ||
        !event.currentTarget.contains(target) ||
        target?.closest("[data-screenshot-help]")
      )
        return;

      if (event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        toggleAutoplay();
      } else if (targetTag === "SELECT") {
        return;
      } else if (event.key === "ArrowRight" && screenshots.length > 1) {
        event.preventDefault();
        event.stopPropagation();
        next();
      } else if (event.key === "ArrowLeft" && screenshots.length > 1) {
        event.preventDefault();
        event.stopPropagation();
        prev();
      }
    },
    [next, prev, screenshots.length, toggleAutoplay],
  );

  if (screenshots.length === 0) return null;

  return (
    <div
      ref={carouselRef}
      tabIndex={-1}
      onKeyDownCapture={handleKeyDown}
      className={`relative bg-surface outline-none ${fullscreen ? "flex h-full w-full min-h-0 flex-col p-2" : "rounded-xl p-4 shadow-lg"} ${className}`}
    >
      <div
        className={`flex items-center justify-center ${fullscreen ? "min-h-0 flex-1" : ""}`}
        aria-busy={current === undefined}
      >
        {visibleImage ? (
          <img
            src={visibleImage.url}
            onError={() => {
              setLoaded(previous => new Map(previous).set(visibleImage.key, null));
              setDisplayed(null);
            }}
            alt={visibleImage.label}
            className={`${
              fullscreen ? "h-full w-full object-contain" : "max-h-[72vh] max-w-full object-contain"
            } transition ${dimmed ? "opacity-40 grayscale" : ""}`}
          />
        ) : current === null ? (
          <div className="flex h-64 w-96 max-w-full flex-col items-center justify-center gap-2 text-text-dim">
            <Image className="h-8 w-8 opacity-40" />
            <span className="text-sm">{t("failed_to_load_screenshot")}</span>
          </div>
        ) : (
          <div className="flex h-64 w-96 max-w-full items-center justify-center text-text-dim">
            {t("loading_ellipsis")}
          </div>
        )}
      </div>
      <div
        className={`${fullscreen ? "mt-2 shrink-0" : "mt-3"} flex flex-wrap items-center justify-center gap-3 text-xs text-text-dim`}
      >
        <button
          type="button"
          onClick={prev}
          disabled={screenshots.length <= 1}
          aria-label={t("previous_screenshot")}
          title={t("previous_screenshot")}
          className="rounded-full border border-outline bg-muted-surface p-1.5 shadow-sm transition enabled:text-text-dim enabled:hover:text-text disabled:opacity-30"
        >
          <ChevronLeft className="h-5 w-5 -translate-x-px" />
        </button>
        <span className="rounded-full border border-outline bg-muted-surface px-3 py-1 text-xs text-text-dim shadow-sm tabular-nums">
          {counterLabel ?? `${index + 1} / ${screenshots.length}`}
        </span>
        <button
          type="button"
          onClick={next}
          disabled={screenshots.length <= 1}
          aria-label={t("next_screenshot")}
          title={t("next_screenshot")}
          className="rounded-full border border-outline bg-muted-surface p-1.5 shadow-sm transition enabled:text-text-dim enabled:hover:text-text disabled:opacity-30"
        >
          <ChevronRight className="h-5 w-5 translate-x-px" />
        </button>
        <button
          type="button"
          onClick={toggleAutoplay}
          disabled={screenshots.length <= 1}
          aria-label={autoplay ? t("pause_screenshot_autoplay") : t("play_screenshot_autoplay")}
          title={autoplay ? t("pause_screenshot_autoplay") : t("play_screenshot_autoplay")}
          className={`rounded-full border border-outline p-1.5 shadow-sm transition disabled:opacity-30 ${
            autoplay
              ? "bg-primary text-white hover:bg-primary/90"
              : "bg-muted-surface text-text-dim hover:text-text"
          }`}
        >
          {autoplay ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-px" />}
        </button>
        <select
          value={playbackSpeed}
          onChange={event =>
            setPlaybackSpeed(Number(event.target.value) as ScreenshotPlaybackSpeed)
          }
          disabled={screenshots.length <= 1}
          aria-label={t("screenshot_playback_speed")}
          title={t("screenshot_playback_speed")}
          className="rounded-full border border-outline bg-muted-surface px-2 py-1 text-xs font-medium text-text-dim shadow-sm transition hover:text-text disabled:opacity-30"
        >
          {SCREENSHOT_PLAYBACK_SPEEDS.map(speed => (
            <option key={speed} value={speed}>
              {formatPlaybackSpeed(speed)}
            </option>
          ))}
        </select>
        {currentScreenshot && slideAction?.(currentScreenshot)}
        {navigationDetails && (
          <span className="whitespace-nowrap tabular-nums">{navigationDetails}</span>
        )}
      </div>
    </div>
  );
};

export default ScreenshotCarousel;
